// External Dependencies
import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { NgbModule, NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { JwtModule } from "@auth0/angular-jwt";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule } from "@angular/common/http";
import {
  MissingTranslationHandler,
  TranslateModule,
} from "@ngx-translate/core";
import { QRCodeModule } from "angularx-qrcode";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CarouselModule } from "ngx-owl-carousel-o";
import { ToastrModule } from "ngx-toastr";
import { InlineSVGModule } from "ng-inline-svg-2";
// Routing & Configuration
import { AppRoutingModule } from "./app-routing.module";
// Feature Modules
import { SharedModule } from "./shared/shared.module";
import { ClubModule } from "./modules/club/club.module";
import { UserModule } from "./modules/user/user.module";
import { LoyaltyModule } from "./modules/loyalty/loyalty.module";
import { VouchersModule } from "./modules/vouchers/vouchers.module";
import { EcashModule } from "./modules/ecash/ecash.module";
import { PurchaseModule } from "./modules/purchase/purchase.module";
import { GuardianModule } from "./modules/guardian/guardian.module";
import { FantasyModule } from "./modules/fantasy/fantasy.module";

// Utilities
import { MomentDateFormatter } from "./shared/utilities/moment-date-formatter";
// @fgb/core
import {
  PopulationGuard,
  ProductCodeGuard,
  jwtTokenGetter,
  FGBMissingTranslationHandler,
  provideAppConfig,
} from "@fgb/core";

// Component Library
import { CardTokenListModule } from "@fgb/portal-component-library/src/lib/ecash/card-token-list";
import { EcashDescriptionModule } from "@fgb/portal-component-library/src/lib/ecash/ecash-description";
import { LoadingModule } from "@fgb/portal-component-library/src/lib/shared/loading";
import { ErrorsModule } from "@fgb/portal-component-library/src/lib/shared/errors";
import { EarnPointsModule } from "@fgb/portal-component-library/src/lib/club/earn-points";
import { FaqSectionModule } from "@fgb/portal-component-library/src/lib/club/faq-section";
import { TermsAndConditionsModule } from "@fgb/portal-component-library/src/lib/club/terms-and-conditions";
import { CountryAndRegionsModule } from "@fgb/portal-component-library/src/lib/shared/country-and-regions";
import { DirectivesModule } from "@fgb/portal-component-library/src/lib/directives";
import { AuctionDetailsModule } from "@fgb/portal-component-library/src/lib/loyalty/auctions/auction-details";
import { AuctionPanelModule } from "@fgb/portal-component-library/src/lib/loyalty/auctions/auction-panel";
import { AuctionListModule } from "@fgb/portal-component-library/src/lib/loyalty/auctions/auction-list";
import { LottoDetailsModule } from "@fgb/portal-component-library/src/lib/loyalty/lottos/lotto-details";
import { LottosListItemModule } from "@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-list-item";
import { LottosListModule } from "@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-list";
import { MarketplaceDetailsModule } from "@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-details";
import { MarketplaceListItemModule } from "@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list-item";
import { MarketplaceListModule } from "@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list";
import {
  BadgeListItemModule,
  BadgeOverviewListModule,
  BadgeFeaturedListModule,
  BadgeAchievedModule,
} from "@fgb/portal-component-library/src/lib/loyalty/badges";
import { FeaturedRewardsModule } from "@fgb/portal-component-library/src/lib/loyalty/featured-rewards";
import { AddressBookModule } from "@fgb/portal-component-library/src/lib/user/address-book";
import { ShortLongDescriptionModule } from "@fgb/portal-component-library/src/lib/shared/short-long-description";
import { ContactUsModule } from "@fgb/portal-component-library/src/lib/club/contact-us";

// General Pages
import { AppComponent } from "./app.component";
import { MasterPageComponent } from "./pages/master/master-page/master-page.component";
import { HomePageComponent } from "./pages/home-page/home-page.component";
import { LoginPageComponent } from "./pages/login-page/login-page.component";
import { LoginAccountsPageComponent } from "./pages/login-accounts-page/login-accounts-page.component";
import { MaintenancePageComponent } from "./pages/maintenance-page/maintenance-page.component";
import { TermsPageComponent } from "./pages/terms-page/terms-page.component";
import { FaqPageComponent } from "./pages/faq-page/faq-page.component";
import { ContactUsPageComponent } from "./pages/contact-us-page/contact-us-page.component";
import { NotFoundPageComponent } from "./pages/not-found-page/not-found-page.component";

// Loyalty Pages
import { RewardsLayoutComponent } from "./pages/rewards-layout/rewards-layout.component";
import { RewardsPageComponent } from "./pages/rewards-page/rewards-page.component";
import { AuctionsPageComponent } from "./pages/auctions-page/auctions-page.component";
import { LottosPageComponent } from "./pages/lottos-page/lottos-page.component";
import { MarketplacePageComponent } from "./pages/marketplace-page/marketplace-page.component";
import { EventsPageComponent } from "./pages/events-page/events-page.component";
import { WalletPageComponent } from "./pages/wallet-page/wallet-page.component";
import { AuctionDetailPageComponent } from "./pages/auction-detail-page/auction-detail-page.component";
import { LottoDetailPageComponent } from "./pages/lotto-detail-page/lotto-detail-page.component";
import { MarketplaceDetailPageComponent } from "./pages/marketplace-detail-page/marketplace-detail-page.component";
import { LottosSuccessPageComponent } from "./pages/lottos-page/lottos-success-page/lottos-success-page.component";
import { MarketplacePurchaseSuccessPageComponent } from "./pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component";
import { LottoWalletDetailsPageComponent } from "./pages/lotto-wallet-details-page/lotto-wallet-details-page.component";
// Polls, Surveys & Trivia (Games)
import { GamesLayoutComponent } from "./pages/games-layout/games-layout.component";
import { GamesPageComponent } from "./pages/games-page/games-page.component";
import {
  QuizPageComponent,
  QuizPlayPageComponent,
} from "./pages/quiz-page/quiz-page.component";
import { QuizLeaderboardPageComponent } from "./pages/quiz-leaderboard-page/quiz-leaderboard-page.component";
import { PollsPageComponent } from "./pages/polls-page/polls-page.component";
import {
  FantasyGamesPageComponent,
  FantasyGamesPlayPageComponent,
} from "./pages/fantasy-games-page/fantasy-games-page.component";
import { FantasyPlayerSelectPageComponent } from "./pages/fantasy-player-select-page/fantasy-player-select-page.component";
import {
  PredictorPageComponent,
  PredictorPlayComponent,
} from "./pages/predictor-page/predictor-page.component";
import { PredictorLeaderboardPageComponent } from "./pages/predictor-leaderboard-page/predictor-leaderboard-page.component";
// Account Pages
import { AccountDetailsPageComponent } from "./pages/account-details-page/account-details-page.component";
import { ActivityPageComponent } from "./pages/activity-page/activity-page.component";
// Ecash Pages
import { EcashPageComponent } from "./pages/ecash-page/ecash-page.component";
import { EcashResultPageComponent } from "./pages/ecash-page/ecash-result-page/ecash-result-page.component";
import { EcashSuccessPageComponent } from "./pages/ecash-page/ecash-success-page/ecash-success-page.component";
import { EcashFailurePageComponent } from "./pages/ecash-page/ecash-failure-page/ecash-failure-page.component";
import { EcashHistoryPageComponent } from "./pages/ecash-history/ecash-history.component";
// Portal Products
import { PortalProductResultsPageComponent } from "./pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component";
import { PortalProductSuccessPageComponent } from "./pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component";
import { PortalProductFailurePageComponent } from "./pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component";

// Club Pages
import { EarnPageComponent } from "./pages/earn-page/earn-page.component";
import { GuardianPageComponent } from "./pages/guardian-page/guardian-page.component";
import { LayoutModule } from "./modules/layouts/layout.module";

import { LottosTermsPageComponent } from "./pages/lottos-terms-page/lottos-terms-page.component";
import { MarketplaceTermsPageComponent } from "./pages/marketplace-terms-page/marketplace-terms-page.component";
import { WorksheetPageComponent } from "./pages/worksheet-page/worksheet-page.component";
import { FantasyLeaderboardPageComponent } from "./pages/fantasy-leaderboard-page/fantasy-leaderboard-page.component";
import { SubscriptionsPageComponent } from "./pages/subscriptions-page/subscriptions-page.component";
import { CartCheckoutPagePortalProductsComponent } from "./pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component";
import { CartCheckoutPageComponent } from "./pages/cart-checkout-page/cart-checkout-page.component";
import { CartCheckoutResultsPageComponent } from "./pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component";
import { CartCheckoutSuccessPageComponent } from "./pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component";
import { ProductsPageComponent } from "./pages/products-page/products-page.component";
import { PaymentPageComponent } from "./pages/payment-page/payment-page.component";
import { PaymentLayoutComponent } from "./pages/payment-layout/payment-layout.component";
import { CardLinkModule } from "@fgb/portal-component-library/src/lib/cardlinking";
import { SharedSearchBarModule } from "@fgb/portal-component-library/src/lib/shared/shared-search-bar";
import { AnnouncementsModule } from "@fgb/portal-component-library/src/lib/club/announcements";
import { CountdownModule } from "@fgb/portal-component-library/src/lib/shared/countdown";
import { OfferSearchBarModule } from "@fgb/portal-component-library/src/lib/club/offer-search-bar";
import { CartItemModule } from "@fgb/portal-component-library/src/lib/loyalty/cart-item";
import { ClubSelectorModule } from "@fgb/portal-component-library/src/lib/user/club-selector";
import { EditQuestionsModule } from "@fgb/portal-component-library/src/lib/user/edit-questions";
import { CampaignDetailsPageComponent } from "./pages/campaign-details-page/campaign-details-page.component";
import { CampaignV2DetailsOfferPageComponent } from "./pages/campaign-v2-details-offer-page/campaign-v2-details-offer-page.component";
import { CampaignV2DetailsVoucherPageComponent } from "./pages/campaign-v2-details-voucher-page/campaign-v2-details-voucher-page.component";
import { CampaignsV2OffersListComponent } from "./modules/club/components/campaigns-v2/campaigns-v2-offers-list/campaigns-v2-offers-list.component";
import { CampaignsV2UsedOffersListComponent } from "./modules/club/components/campaigns-v2/campaigns-v2-used-offers-list/campaigns-v2-used-offers-list.component";
import { CampaignsV2ListItemComponent } from "./modules/club/components/campaigns-v2/campaigns-v2-list-item/campaigns-v2-list-item.component";
import { CampaignsV2VoucherListItemComponent } from "./modules/club/components/campaigns-v2/campaigns-v2-voucher-list-item/campaigns-v2-voucher-list-item.component";
import { CampaignsV2UsedListItemComponent } from "./modules/club/components/campaigns-v2/campaigns-v2-used-list-item/campaigns-v2-used-list-item.component";
import { RegistrationLayoutPageComponent } from "./modules/registration/pages/registration-layout-page/registration-layout-page.component";
import { WalletClaimedPageComponent } from "./pages/wallet-claimed-page/wallet-claimed-page.component";
import { NotificationModule } from "@fgb/portal-component-library/src/lib/club/notification";
import { WelcomeComponent } from "./modules/club/components/welcome/welcome.component";
import { WalletModule } from "@fgb/portal-component-library/src/lib/loyalty/wallet";
import { FeedModule } from "@fgb/portal-component-library/src/lib/feeds";
import { MarketplaceWalletPageComponent } from "./pages/marketplace-wallet-page/marketplace-wallet-page.component";
import { AddPaymentCardDetailModule } from "@fgb/portal-component-library/src/lib/loyalty/add-payment-card-detail";
import { ScorecardModule } from "@fgb/portal-component-library/src/lib/loyalty/scorecards";
import { AuctionWalletDetailsPageComponent } from "./pages/auction-wallet-details-page/auction-wallet-details-page.component";
import { RouterModule } from "@angular/router";
import { FilterModalModule } from "./pages/rewards-page/filter-modal";
import { TicketTransferModule } from "@fgb/portal-component-library/src/lib/ticket-transfer";
import { MixedLoyaltyListModule } from "@fgb/portal-component-library/src/lib/loyalty/mixed-loyalty";
import { ChallengesPageComponent } from "./pages/challenges-page/challenges-page.component";
import { GamesModule } from "@fgb/portal-component-library/src/lib/games";
import { TabComponent } from "./modules/club/components/games-tab/tab.component";
import { CampaignsModule } from "@fgb/portal-component-library/src/lib/campaigns";
import { AccountLayoutPageComponent } from "./pages/account-layout-page/account-layout-page.component";
import { buildPaymentTemplates } from "@fgb/portal-component-library/src/lib/shared/payment";
import { SplitTransactionModule } from "./modules/user/components/split-transaction/split-transaction.module";
import { ExpressCheckoutModule } from "@fgb/portal-component-library/src/lib/shared/payment/express-checkout";

@NgModule({
  declarations: [
    // General Pages
    AppComponent,
    MasterPageComponent,
    HomePageComponent,
    LoginPageComponent,
    LoginAccountsPageComponent,
    MaintenancePageComponent,
    TermsPageComponent,
    FaqPageComponent,
    ContactUsPageComponent,
    NotFoundPageComponent,
    // Loyalty Pages

    RewardsLayoutComponent,
    RewardsPageComponent,
    AuctionsPageComponent,
    LottosPageComponent,
    MarketplacePageComponent,
    EventsPageComponent,
    WalletPageComponent,
    AuctionDetailPageComponent,
    LottoDetailPageComponent,
    MarketplaceDetailPageComponent,
    LottosSuccessPageComponent,
    MarketplacePurchaseSuccessPageComponent,
    LottoWalletDetailsPageComponent,
    AuctionWalletDetailsPageComponent,
    // Polls, Surveys & Trivia Pages (Games)
    GamesLayoutComponent,
    GamesPageComponent,
    QuizPageComponent,
    QuizPlayPageComponent,
    QuizLeaderboardPageComponent,
    PollsPageComponent,
    FantasyGamesPageComponent,
    FantasyPlayerSelectPageComponent,
    FantasyGamesPlayPageComponent,
    PredictorPageComponent,
    PredictorPlayComponent,
    PredictorLeaderboardPageComponent,

    // Account Pages
    AccountDetailsPageComponent,
    ActivityPageComponent,
    // Ecash Pages
    EcashPageComponent,
    EcashResultPageComponent,
    EcashSuccessPageComponent,
    EcashFailurePageComponent,
    EcashHistoryPageComponent,
    PaymentPageComponent,
    PaymentLayoutComponent,
    PortalProductResultsPageComponent,
    PortalProductSuccessPageComponent,
    PortalProductFailurePageComponent,
    // Guardians Pages
    GuardianPageComponent,
    // Club Pages
    EarnPageComponent,
    LottosTermsPageComponent,
    MarketplaceTermsPageComponent,
    WorksheetPageComponent,
    FantasyLeaderboardPageComponent,
    SubscriptionsPageComponent,
    CartCheckoutPagePortalProductsComponent,
    CartCheckoutPageComponent,
    CartCheckoutSuccessPageComponent,
    CartCheckoutResultsPageComponent,
    ProductsPageComponent,
    CampaignDetailsPageComponent,
    CampaignV2DetailsOfferPageComponent,
    CampaignV2DetailsVoucherPageComponent,
    CampaignsV2OffersListComponent,
    CampaignsV2UsedOffersListComponent,
    CampaignsV2ListItemComponent,
    CampaignsV2VoucherListItemComponent,
    CampaignsV2UsedListItemComponent,
    WelcomeComponent,
    // Registration
    RegistrationLayoutPageComponent,
    WalletClaimedPageComponent,
    MarketplaceWalletPageComponent,
    ChallengesPageComponent,
    AccountLayoutPageComponent,
  ],
  imports: [
    // External Modules
    RouterModule,
    CarouselModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    InlineSVGModule.forRoot(),
    QRCodeModule,
    BrowserAnimationsModule,
    EarnPointsModule,
    FaqSectionModule,
    TermsAndConditionsModule,
    AuctionDetailsModule,
    AuctionPanelModule,
    AuctionListModule,
    LottoDetailsModule,
    LottosListModule,
    LottosListItemModule,
    ScorecardModule,
    BadgeListItemModule,
    BadgeOverviewListModule,
    BadgeFeaturedListModule,
    BadgeAchievedModule,
    MarketplaceDetailsModule,
    ShortLongDescriptionModule,
    MarketplaceListModule,
    MarketplaceListItemModule,
    MixedLoyaltyListModule,
    LoadingModule,
    CountryAndRegionsModule,
    EcashDescriptionModule,
    CardTokenListModule,
    CountdownModule,
    ErrorsModule,
    EditQuestionsModule,
    FeaturedRewardsModule,
    AddressBookModule,
    WalletModule,
    NotificationModule,
    ContactUsModule,
    CampaignsModule,
    AddPaymentCardDetailModule,
    FilterModalModule,
    TicketTransferModule,
    FeedModule,
    ExpressCheckoutModule,
    ToastrModule.forRoot({
      maxOpened: 5,
    }),
    JwtModule.forRoot({
      config: {
        allowedDomains: ["http://localhost/portal2/api"],
        tokenGetter: jwtTokenGetter,
      },
    }),
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FGBMissingTranslationHandler,
      },
    }),
    // Feature Modules
    LayoutModule,
    GuardianModule,
    PurchaseModule,
    ClubModule,
    EcashModule,
    VouchersModule,
    UserModule,
    SharedModule,
    FantasyModule,
    LoyaltyModule,
    DirectivesModule,
    CardLinkModule,
    AnnouncementsModule,
    OfferSearchBarModule,
    SharedSearchBarModule,
    CartItemModule,
    ClubSelectorModule,
    GamesModule,
    TabComponent,
    SplitTransactionModule,
  ],
  providers: [
    provideAppConfig({
      dateOptions: {
        LongDateFormat: "MMM D, yyyy h:mma",
      },
      paymentTemplates: buildPaymentTemplates(),
    }),

    PopulationGuard,
    ProductCodeGuard,
    { provide: NgbDateParserFormatter, useClass: MomentDateFormatter },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
